import { AfterViewInit, Component, ElementRef, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild } from '@angular/core';
import { DrivertraceService, VehiclePositionAndDrivers } from '../../../../services/drivertrace/drivertrace.service';
import { TranslateService } from '@ngx-translate/core';

// @ts-ignore
declare const L: any;

@Component({
    selector: 'app-vehicle-map',
    templateUrl: './vehicle-map.component.html',
    styleUrls: ['./vehicle-map.component.css']
})
export class VehicleMapComponent implements AfterViewInit, OnChanges, OnDestroy {

    @Input() vehiclePositionAndDrivers: VehiclePositionAndDrivers[] = [];
    @Input() companyId: number;

    @ViewChild('mapContainer') mapContainer!: ElementRef;
    map: any;
    searchFilter = '';
    loading = false;

    get filteredVehicles(): VehiclePositionAndDrivers[] {
        if (!this.searchFilter.trim()) {
            return this.vehiclePositionAndDrivers;
        }
        const q = this.searchFilter.trim().toLowerCase();
        return this.vehiclePositionAndDrivers.filter(v =>
            v.registrationNumber?.toLowerCase().includes(q) ||
            v.vin?.toLowerCase().includes(q)
        );
    }

    constructor(
        private _translate: TranslateService,
        private drivertraceService: DrivertraceService
    ) { }

    ngAfterViewInit(): void {
        const mapContainer = this.mapContainer.nativeElement;
        // @ts-ignore
        this.map = L.map(mapContainer).setView([51.505, -0.09], 4);
        // @ts-ignore
        L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        }).addTo(this.map);
        this.map.setView([51.505, -0.09], 4);
        setTimeout(() => {
            window.dispatchEvent(new Event('resize'));
        }, 10);

        this.fetchPositionsAndUpdate();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['vehiclePositionAndDrivers'] && this.map) {
            this.fetchPositionsAndUpdate();
        }
        if (changes['companyId'] && this.map && !changes['vehiclePositionAndDrivers']) {
            this.fetchPositionsAndUpdate();
        }
    }

    ngOnDestroy(): void {
        if (this.map) {
            this.map.remove();
            this.map = null;
        }
    }

    onTabVisible(): void {
        setTimeout(() => {
            if (this.map) {
                this.map.invalidateSize();
            }
        }, 100);
    }

    onClickShowOnMap(pos: VehiclePositionAndDrivers): void {
        this.map.setView([pos.lat, pos.lng], 15);
    }

    isRecent(pos: VehiclePositionAndDrivers): boolean {
        if (!pos.lastPositionTime) return false;
        const diff = Date.now() - new Date(pos.lastPositionTime).getTime();
        return diff < 3600000;
    }

    private fetchPositionsAndUpdate(): void {
        if (!this.companyId) {
            this.updateMarkers();
            return;
        }

        this.loading = true;
        this.drivertraceService.getVehiclesListV2Basic(this.companyId).subscribe(vehiclesResp => {
            if (vehiclesResp && vehiclesResp.Records) {
                const hadData = this.vehiclePositionAndDrivers.length > 0;
                for (const vehicle of vehiclesResp.Records) {
                    const existing = this.vehiclePositionAndDrivers.find(v => v.vin === vehicle.identificationNumber);
                    if (existing && vehicle.lastPosition) {
                        existing.lat = vehicle.lastPosition.latitude;
                        existing.lng = vehicle.lastPosition.longitude;
                        existing.address = vehicle.lastPosition.address;
                        existing.lastPositionTime = vehicle.lastPosition.fixTime;
                        existing.speed = vehicle.lastPosition.speed;
                        existing.outdated = vehicle.lastPosition.outdated;
                    } else if (!existing && vehicle.lastPosition && !hadData) {
                        this.vehiclePositionAndDrivers.push({
                            lat: vehicle.lastPosition.latitude,
                            lng: vehicle.lastPosition.longitude,
                            vin: vehicle.identificationNumber,
                            registrationNumber: vehicle.registrationNumber,
                            drivers: [],
                            address: vehicle.lastPosition.address,
                            lastPositionTime: vehicle.lastPosition.fixTime,
                            speed: vehicle.lastPosition.speed,
                            outdated: vehicle.lastPosition.outdated,
                        });
                    }
                }
            }
            this.loading = false;
            this.updateMarkers();
        }, () => {
            this.loading = false;
            this.updateMarkers();
        });
    }

    private updateMarkers(): void {
        if (!this.map) {
            return;
        }

        this.map.invalidateSize();

        const data = this.vehiclePositionAndDrivers;
        const realPositions = data.filter(position => position.lat && position.lng);

        this.map.eachLayer((layer) => {
            // @ts-ignore
            if (layer instanceof L.Marker) {
                this.map.removeLayer(layer);
            }
        });

        for (const position of realPositions) {
            // @ts-ignore
            const marker = L.marker([position.lat, position.lng]).addTo(this.map);
            let popupContent = `<strong>${position.registrationNumber}</strong><br>`;
            if (position.address) {
                popupContent += `${position.address}<br>`;
            }
            if (position.drivers.length > 0) {
                popupContent += `${this._translate.instant('Drivers')}: ${position.drivers.join(', ')}`;
            }
            marker.bindPopup(popupContent);
        }

        // Zoom to whole Europe view
        this.map.setView([50, 15], 4);
    }
}
